import React, { useContext, useState } from "react"
import styled from "styled-components"
import { Modal, Select } from "antd"
import { ShopOutlined } from "@ant-design/icons"
import UserContext from "contexts/user/context"
import useOpus from "contexts/opus/hooks/use-opus"
import { isWorkInProgressPath } from "utils/work-in-progress"

const StyledBranch = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5ch;
  padding: 0 1ch;
`

const StyledSelect = styled(Select)`
  min-width: 180px;
`

/**
 * Shows the branch (office) the user is working in, and lets users that belong to more than one branch switch between them
 */
export const BranchSwitcher: React.FunctionComponent = () => {
  // the header can be rendered outside of the UserProvider (eg settings), so don't use the throwing hook
  const { activeBranch, branches = [] } = useContext(UserContext)
  const { switchBranch } = useOpus()
  const [isSwitching, setIsSwitching] = useState(false)

  if (!activeBranch) {
    return null
  }

  if (branches.length <= 1) {
    return (
      <StyledBranch data-testid="branch-name">
        <ShopOutlined />
        {activeBranch.name}
      </StyledBranch>
    )
  }

  const doSwitch = async (companyID: string) => {
    setIsSwitching(true)
    try {
      await switchBranch(companyID)
    } finally {
      setIsSwitching(false)
    }
  }

  const onChange = (companyID: unknown) => {
    // Switching office takes the user to the clients list and clears what's loaded,
    // so ask first when they're in the middle of something - the dropdown stays on the current office until they confirm
    if (isWorkInProgressPath(window.location.pathname)) {
      Modal.confirm({
        title: "Switch office?",
        content:
          "Switching office closes what you're working on. Any changes you haven't saved will be lost.",
        okText: "Switch office",
        cancelText: "Stay here",
        centered: true,
        onOk: () => doSwitch(companyID as string)
      })
      return
    }

    return doSwitch(companyID as string)
  }

  return (
    <StyledBranch>
      <ShopOutlined />
      <StyledSelect
        aria-label="Office"
        data-testid="branch-switcher"
        value={activeBranch.id}
        loading={isSwitching}
        disabled={isSwitching}
        onChange={onChange}
        options={branches.map((branch) => ({
          label: branch.name,
          value: branch.id
        }))}
      />
    </StyledBranch>
  )
}

export default BranchSwitcher
